import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricing } from '../contexts/PricingContext';
import { ContactFormPopup } from './ContactFormPopup';
export function HeroVariant60() {
  const {
    language
  } = useLanguage();
  const {
    price
  } = usePricing();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const content = {
    ru: {
      badge: 'Профессиональное веб-агентство',
      title1: 'Сделаем сайт для вас ',
      titleFree: 'бесплатно.',
      title2: 'Обслуживание — ',
      titlePrice: `${price}/мес.`,
      sub: `Мы делаем сайт за свой счёт. Вы смотрите, просите правки, утверждаете. Когда всё нравится — платите ${price} в месяц за хостинг и поддержку. Не понравится — просто уходите. Без оплаты.`,
      cta: 'Получить бесплатный макет'
    },
    ua: {
      badge: 'Професійне веб-агентство',
      title1: 'Зробимо сайт для вас ',
      titleFree: 'безкоштовно.',
      title2: 'Обслуговування — ',
      titlePrice: `${price}/міс.`,
      sub: `Ми робимо сайт за свій рахунок. Ви дивитеся, просите правки, затверджуєте. Коли все подобається — платите ${price} на місяць за хостинг та підтримку. Не сподобається — просто йдете. Без оплати.`,
      cta: 'Отримати безкоштовний макет'
    },
    en: {
      badge: 'Professional web agency',
      title1: 'We build your website ',
      titleFree: 'for free.',
      title2: 'Maintenance — ',
      titlePrice: `${price}/mo.`,
      sub: `We build the site at our expense. You review, request edits, and approve. When you love it, you pay ${price}/month for hosting and support. Don't like it? Just walk away. No charge.`,
      cta: 'Get a free mockup'
    }
  }[language] || content.ru;
  return <>
      <section className="relative min-h-screen w-full overflow-hidden px-4 pt-24 md:pt-28 pb-12 md:pb-16 flex flex-col justify-center" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-848">
        {/* Subtle background accent matching AiCoursePage */}
        <div className="absolute inset-0 opacity-20 pointer-events-none" data-id="element-849">
          <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-[100px]" style={{
          background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
        }} data-id="element-850" />
        </div>

        <div className="relative z-20 mx-auto flex w-full max-w-6xl flex-col md:flex-row items-center justify-center gap-8 md:gap-12 mt-0 md:mt-8 lg:mt-0" data-id="element-851">
          {/* Left side - Content */}
          <div className="w-full md:flex-1 text-center md:text-left" data-id="element-852">
            {/* Badge */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 md:mb-6 border" style={{
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--border)',
            color: 'var(--text-secondary)'
          }} data-id="element-853">
              <span className="text-xs md:text-sm font-semibold tracking-wide flex items-center gap-1.5" data-id="element-854">
                🔥 {content.badge}
              </span>
            </motion.div>

            {/* Heading */}
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="mb-6 md:mb-8 text-4xl leading-tight tracking-tight md:text-5xl lg:text-[3.5rem] font-bold" style={{
            color: 'var(--text-primary)'
          }} data-id="element-855">
              <span className="block mb-1" data-id="element-856">
                {content.title1}
                <span style={{
                color: 'var(--accent)'
              }} data-id="element-857">
                  {content.titleFree}
                </span>
              </span>
              <span className="block text-3xl md:text-4xl lg:text-5xl mt-2" style={{
              color: 'var(--text-secondary)'
            }} data-id="element-858">
                {content.title2}
                <span style={{
                color: 'var(--text-primary)'
              }} data-id="element-859">
                  {content.titlePrice}
                </span>
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="text-lg md:text-xl mb-10 font-medium max-w-lg mx-auto md:mx-0 leading-relaxed" style={{
            color: 'var(--text-secondary)'
          }} data-id="element-860">
              {content.sub}
            </motion.p>

            {/* CTA Button */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="flex justify-center md:justify-start" data-id="element-861">
              <button onClick={() => setIsPopupOpen(true)} className="group flex items-center justify-center gap-2 rounded-2xl px-10 py-5 font-bold text-lg transition-all hover:scale-105 shadow-md hover:shadow-lg w-full sm:w-auto" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-862">
                {content.cta}
                <ArrowRightIcon className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-863" />
              </button>
            </motion.div>
          </div>

          {/* Right side - Video */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="w-full md:flex-1 relative mt-12 md:mt-0 flex items-center justify-center" data-id="element-864">
            <div className="relative w-full max-w-[480px] mx-auto rounded-3xl overflow-hidden shadow-2xl border bg-white" style={{
            borderColor: 'var(--border)'
          }} data-id="element-865">
              <video src="https://cdn.magicpatterns.com/uploads/vrXe21wkVqS62d5BzpZ9eJ/original-2de319f4767c5a0d4acf4f107b938504.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-id="element-866" />
            </div>
          </motion.div>
        </div>
      </section>
      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-867" />
    </>;
}